// Single row in the category tree. Stateless w.r.t. selection — caller passes
// `selectedId` and `onSelect`. Expansion is local (matches user mental model:
// expanding a branch is independent of which row you're inspecting).

import { useState } from "react";
import { ChevronRight, Folder, FolderOpen } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import type { Category } from "./types";

interface Props {
  cat: Category;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** When the search query is non-empty, force-expand every branch so matches are visible. */
  forceExpanded: boolean;
}

export function CategoryTreeNode({ cat, depth, selectedId, onSelect, forceExpanded }: Props) {
  const [open, setOpen] = useState(true);
  const expanded = forceExpanded || open;
  const hasChildren = cat.children && cat.children.length > 0;
  const isSelected = selectedId === cat.id;

  function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    setOpen((v) => !v);
  }

  return (
    <li role="treeitem" aria-expanded={hasChildren ? expanded : undefined} aria-selected={isSelected}>
      <div
        onClick={() => onSelect(cat.id)}
        className={cn(
          "group flex items-center gap-2 rounded-md py-1.5 pr-2 cursor-pointer transition-colors",
          "min-h-[36px]",
          isSelected
            ? "bg-accent text-accent-foreground"
            : "hover:bg-muted/60",
        )}
        style={{ paddingLeft: 8 + depth * 16 }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={toggle}
            className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground"
            aria-label={expanded ? "Thu gọn" : "Mở rộng"}
          >
            <ChevronRight
              size={14}
              className={cn("transition-transform", expanded && "rotate-90")}
            />
          </button>
        ) : (
          <span className="w-5" aria-hidden />
        )}

        {expanded && hasChildren ? (
          <FolderOpen size={14} className="shrink-0 text-muted-foreground" />
        ) : (
          <Folder size={14} className="shrink-0 text-muted-foreground" />
        )}

        <span className="flex-1 truncate text-sm">{cat.name}</span>

        <span className="tabular-nums text-xs text-muted-foreground">
          {cat.productCount}
        </span>
      </div>

      {expanded && hasChildren && (
        <ul role="group" className="space-y-0.5">
          {cat.children.map((child) => (
            <CategoryTreeNode
              key={child.id}
              cat={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              forceExpanded={forceExpanded}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
